import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import config from '../config';
import { notifyRbacUpdated } from './rbac';
import { getBrand } from './brand';

const RESTAURANT_PROPERTY = 'R';
// Domain decides the login logo: restaurant host → DineMaster (222.png),
// hotel host → Hotel Desk (LOGO 12 / hoteldesk.png).
const BRAND = getBrand();

/** Matches account/init: propertType, PropertyType, propertyType */
const normalizePropertyType = (data) => {
  const v =
    data?.propertType ?? data?.PropertyType ?? data?.propertyType ?? '';
  return String(v).trim().toUpperCase();
};

const isRestaurantProperty = (data) =>
  normalizePropertyType(data) === RESTAURANT_PROPERTY;

/** So /setup → HotelSetupWizard → RestaurantSetupWizard (isRestaurantCompany). */
const withRestaurantCompanyDetails = (data) => {
  const details = { ...data };
  const pt = normalizePropertyType(data);
  if (pt) details.propertType = pt;
  if (isRestaurantProperty(data)) {
    details.isRestaurant = true;
  }
  return details;
};

function extractHotels(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  for (const key of ['data', 'hotels', 'result', 'results', 'items', 'value']) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  if (raw.id || raw.Id || raw.hotelId || raw.HotelId) return [raw];
  return [];
}

export default function Login() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [company, setCompany] = useState(null);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [companyName, setCompanyName] = useState('');
  // ── Email-OTP password reset ──
  const [resetOpen, setResetOpen] = useState(false);
  const [resetStep, setResetStep] = useState(1); // 1 = send OTP, 2 = verify
  const [resetUsername, setResetUsername] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPass, setResetNewPass] = useState('');
  const [resetMsg, setResetMsg] = useState('');
  const [resetMaskedEmail, setResetMaskedEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  // Latest loaded company + the in-flight init() promise. The init API is
  // triggered when the operator LEAVES the company-code field (onBlur), not on
  // every keystroke — keystroke-firing raced the Login click and showed a false
  // "Company not loaded". These refs let handleLogin await whatever load is
  // already running instead of re-firing init on click.
  const companyRef = useRef(null);
  const loadInFlight = useRef(null);
  // Reload logic
  useEffect(() => {
    const hasReloaded = sessionStorage.getItem('loginReloaded');
    if (!hasReloaded) {
      sessionStorage.setItem('loginReloaded', 'true');
      window.location.reload();
    }
  }, []);

  // Load Company Info
  useEffect(() => {
    loadCompany();
  }, []);
  // init(): resolve the company from the typed code. Returns the loaded company
  // (or null on failure) and records the promise so a Login click can await it.
  const loadCompany = (clientValue) => {
    const client = (clientValue || '').trim();
    if (!client) return Promise.resolve(null);

    const promise = (async () => {
      try {
        // SAVE CLIENT HERE
        localStorage.setItem('client', client);

        console.log('🔥 Final Client Used:', client);

        const res = await fetch(
          `${config.API_BASE_URL}/account/init?client=${client}`,
          { method: 'GET' },
        );

        if (!res.ok) throw new Error(`Server error: ${res.status}`);

        const data = await res.json();
        const companyDetails = withRestaurantCompanyDetails(data);
        const propertyType = normalizePropertyType(data);

        console.log('✅ Company Loaded:', companyDetails);

        setCompany(companyDetails);
        companyRef.current = companyDetails;

        sessionStorage.setItem('companyId', companyDetails.id);
        if (propertyType) {
          sessionStorage.setItem('propertType', propertyType);
        }
        sessionStorage.setItem('companyDetails', JSON.stringify(companyDetails));
        return companyDetails;
      } catch (err) {
        console.error('❌ Init Error:', err);
        return null;
      }
    })();

    loadInFlight.current = promise;
    return promise;
  };

  // Guarantees a loaded company without re-firing init on click: reuse the
  // already-loaded one, else await the in-flight blur-triggered load, else (only
  // if the operator never left the field) do a single final load.
  const ensureCompany = async () => {
    if (companyRef.current?.id) return companyRef.current;
    if (loadInFlight.current) await loadInFlight.current;
    if (companyRef.current?.id) return companyRef.current;
    return loadCompany(companyName);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleLogin = async () => {
    if (!form.username || !form.password) {
      alert('Please enter username and password');
      return;
    }

    const comp = await ensureCompany();
    if (!comp?.id) {
      alert('Company not loaded. Please refresh the page.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${config.API_BASE_URL}/account/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: form.username,
          password: form.password,
          companyid: comp.id,
        }),
      });

      if (!response.ok) {
        alert('Wrong username or password');
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.message === 'Login successful') {
        sessionStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('username', data.user.username);
        sessionStorage.setItem('userId', data.user.id);
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('permissions', JSON.stringify(data.permisioon));
        const rbacCodes = data.permissionCodes ?? data.PermissionCodes;
        if (Array.isArray(rbacCodes) && rbacCodes.length > 0) {
          sessionStorage.setItem('permissionCodes', JSON.stringify(rbacCodes));
        } else {
          sessionStorage.removeItem('permissionCodes');
        }
        notifyRbacUpdated();

        // One-time direct-login URL returned by the login API (single-use link).
        if (data.directLoginUrl) {
          sessionStorage.setItem('directLoginUrl', data.directLoginUrl);
        } else {
          sessionStorage.removeItem('directLoginUrl');
        }

        console.log('✅ Login Success:', data);

        // Ask the dashboard to hard-refresh ONCE on this login (it consumes
        // this flag and reloads a single time). Set per-login so every fresh
        // login gets exactly one refresh — never a loop.
        sessionStorage.setItem('pendingDashboardRefresh', '1');

        // ── First-time setup check ──────────────────────────
        // Hotel (default): no properties → HotelSetupWizard at /setup.
        // Restaurant (propertType R from account/init): incomplete → same route;
        // HotelSetupWizard delegates to RestaurantSetupWizard when isRestaurant is set.
        const isRestaurant = isRestaurantProperty(comp);
        try {
          const hotelRes = await fetch(
            `${config.API_BASE_URL}/Home/GetHotels/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (hotelRes.ok) {
            const raw = await hotelRes.json();
            const hotels = extractHotels(raw);
            const hasProperties = hotels.length > 0;
            if (!hasProperties) {
              localStorage.removeItem('hotelSetupDone');
              navigate('/setup');
              return;
            }
          } else if (isRestaurant) {
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
        } catch (e) {
          if (isRestaurant) {
            console.warn(
              'Restaurant setup check failed, opening setup wizard:',
              e,
            );
            localStorage.removeItem('hotelSetupDone');
            navigate('/setup');
            return;
          }
          console.warn('Hotel check failed, proceeding to dashboard:', e);
        }
        // ────────────────────────────────────────────────────

        // ── Subscription access-check (additive; fails open) ──
        // A company can use HMS only if a current subscription exists and is
        // not expired. On any error we proceed normally so login is never
        // blocked by the check itself.
        try {
          const subRes = await fetch(
            `${config.API_BASE_URL}/Subscription/status/${encodeURIComponent(comp.id)}`,
            { headers: { Authorization: `Bearer ${data.token}` } },
          );
          if (subRes.ok) {
            const sub = await subRes.json();
            sessionStorage.setItem('subscriptionStatus', JSON.stringify(sub));
            if (sub && sub.canAccess === false) {
              // Expired / none → send them to renew instead of the dashboard.
              localStorage.setItem('hotelSetupDone', 'true');
              navigate('/admin/Subscription');
              return;
            }
          }
        } catch (e) {
          console.warn('Subscription check failed, proceeding to dashboard:', e);
        }

        localStorage.setItem('hotelSetupDone', 'true');
        navigate('/admin');
      } else {
        alert('Wrong username or password');
      }
    } catch (error) {
      console.error('❌ Login Error:', error);
      alert('Server error. Please try again later.');
    }

    setLoading(false);
  };

  // Forgot Password Handler
  const handleForgotPassword = async () => {
    if (!form.username) {
      alert('Please enter your username first');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      alert('Company not loaded. Please refresh.');
      return;
    }

    setForgotLoading(true);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/account/forgotpassword`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: form.username,
            companyid: comp.id,
          }),
        },
      );

      const data = await response.json();

      if (response.ok) {
        alert(
          data.message ||
            'Password reset link has been sent to your registered email.',
        );
      } else {
        alert(data.message || 'Failed to process forgot password request.');
      }
    } catch (error) {
      console.error('❌ Forgot Password Error:', error);
      alert('Something went wrong. Please try again later.');
    }

    setForgotLoading(false);
  };

  // ── Email-OTP password reset handlers ──
  const openReset = () => {
    setResetUsername(form.username || '');
    setResetStep(1);
    setResetOtp('');
    setResetNewPass('');
    setResetMsg('');
    setResetMaskedEmail('');
    setResetOpen(true);
  };

  const sendResetOtp = async () => {
    if (!resetUsername.trim()) {
      setResetMsg('Please enter your username.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to send OTP.');
        return;
      }
      setResetMaskedEmail(data.email || '');
      setResetStep(2);
      setResetMsg('OTP sent to your registered email.');
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const submitReset = async () => {
    if (!resetOtp.trim() || !resetNewPass) {
      setResetMsg('Enter the OTP and a new password.');
      return;
    }
    const comp = await ensureCompany();
    if (!comp?.id) {
      setResetMsg('Company not loaded. Please refresh.');
      return;
    }
    setResetLoading(true);
    setResetMsg('');
    try {
      const res = await fetch(`${config.API_BASE_URL}/passwordreset/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: resetUsername.trim(),
          companyId: comp.id,
          otp: resetOtp.trim(),
          newPassword: resetNewPass,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResetMsg(data.error || 'Failed to reset password.');
        return;
      }
      alert('Password reset successful. Please log in with your new password.');
      setResetOpen(false);
    } catch {
      setResetMsg('Something went wrong. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #0f172a 0%, #1A56DB 100%)',
        padding: '20px 10px',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}>
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          padding: '40px 30px',
          borderRadius: 20,
          boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
          backgroundColor: '#fff',
        }}>
        {/* Logo & Powered By */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box
            component='img'
            src={BRAND.logo}
            alt={BRAND.name}
            sx={{
              maxHeight: 95,
              width: 'auto',
              objectFit: 'contain',
              mb: 2,
            }}
          />
          <Typography sx={{ fontSize: 13.5, color: '#666', fontWeight: 500 }}>
            Powered By :- Go-ERP.Live
          </Typography>
        </Box>

        <h2
          style={{
            textAlign: 'center',
            marginBottom: 35,
            color: '#1A56DB',
            fontWeight: 700,
            fontSize: '28px',
          }}>
          Welcome Back
        </h2>
        {/* Company Name */}
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Company Code
          </label>

          <input
            type='text'
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder='Enter company Code'
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: '1.5px solid #ddd',
              fontSize: 16,
              outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#1A56DB')}
            onBlur={(e) => {
              e.target.style.borderColor = '#ddd';
              // Run init() when the operator leaves the field (e.g. tabs to
              // Username or clicks Login) — once, with the full code.
              loadCompany(companyName);
            }}
          />
        </div>
        {/* Username */}
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Username
          </label>
          <input
            type='text'
            name='username'
            value={form.username}
            onChange={handleChange}
            placeholder='Enter your username'
            style={{
              width: '100%',
              padding: '14px 16px',
              borderRadius: 10,
              border: '1.5px solid #ddd',
              fontSize: 16,
              outline: 'none',
              transition: 'all 0.2s',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#1A56DB')}
            onBlur={(e) => (e.target.style.borderColor = '#ddd')}
          />
        </div>

        {/* Password with Show/Hide */}
        <div style={{ marginBottom: 20 }}>
          <label
            style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: '#444',
            }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              name='password'
              value={form.password}
              onChange={handleChange}
              placeholder='Enter your password'
              style={{
                width: '100%',
                padding: '14px 50px 14px 16px',
                borderRadius: 10,
                border: '1.5px solid #ddd',
                fontSize: 16,
                outline: 'none',
                transition: 'all 0.2s',
              }}
              onFocus={(e) => (e.target.style.borderColor = '#1A56DB')}
              onBlur={(e) => (e.target.style.borderColor = '#ddd')}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 4,
              }}>
              {showPassword ? (
                <span style={{ fontSize: 20 }}>🙈</span>
              ) : (
                <span style={{ fontSize: 20 }}>👁️</span>
              )}
            </button>
          </div>
        </div>

        {/* Forgot Password Button — opens email-OTP reset */}
        <div style={{ textAlign: 'right', marginBottom: 30 }}>
          <button
            type='button'
            onClick={openReset}
            style={{
              background: 'none',
              border: 'none',
              color: '#1A56DB',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              textDecoration: 'underline',
            }}>
            Forgot Password?
          </button>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          style={{
            width: '100%',
            padding: '16px',
            borderRadius: 10,
            backgroundColor: loading ? '#93c5fd' : '#1A56DB',
            border: 'none',
            color: 'white',
            fontSize: 17,
            fontWeight: 'bold',
            cursor: loading ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.3s ease',
            marginBottom: 20,
          }}>
          {loading ? 'Logging in...' : 'Login'}
        </button>

        <Typography
          variant='body2'
          sx={{
            textAlign: 'center',
            color: '#555',
            fontSize: 14,
            mb: 1.5,
          }}>
          Don&apos;t have an account?{' '}
          <a
            href='https://signup.goerp.live/Account/Signup'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: '#1A56DB', fontWeight: 700, textDecoration: 'none' }}>
            Sign Up
          </a>
        </Typography>

        <Typography
          variant='body2'
          sx={{
            textAlign: 'center',
            color: '#777',
            fontSize: 13.5,
          }}>
          Need help? Contact your Administrator
        </Typography>
      </div>

      {/* ── Email-OTP password reset modal ── */}
      {resetOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: 16,
          }}>
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              width: '100%',
              maxWidth: 380,
              padding: '26px 24px',
              boxShadow: '0 15px 50px rgba(0,0,0,0.3)',
            }}>
            <h3 style={{ margin: 0, marginBottom: 6, color: '#1A56DB' }}>
              Reset Password
            </h3>
            <p style={{ margin: 0, marginBottom: 16, fontSize: 13, color: '#666' }}>
              {resetStep === 1
                ? 'Apna username daalo — registered email par OTP aayega.'
                : `OTP ${resetMaskedEmail ? `(${resetMaskedEmail})` : ''} aur naya password daalo.`}
            </p>

            {resetStep === 1 ? (
              <input
                value={resetUsername}
                onChange={(e) => setResetUsername(e.target.value)}
                placeholder='Username'
                style={{
                  width: '100%',
                  padding: '13px 14px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd',
                  fontSize: 15,
                  outline: 'none',
                }}
              />
            ) : (
              <>
                <input
                  value={resetOtp}
                  onChange={(e) => setResetOtp(e.target.value)}
                  placeholder='6-digit OTP'
                  style={{
                    width: '100%',
                    padding: '13px 14px',
                    borderRadius: 10,
                    border: '1.5px solid #ddd',
                    fontSize: 15,
                    outline: 'none',
                  }}
                />
                <input
                  type='password'
                  value={resetNewPass}
                  onChange={(e) => setResetNewPass(e.target.value)}
                  placeholder='New password'
                  style={{
                    width: '100%',
                    padding: '13px 14px',
                    borderRadius: 10,
                    border: '1.5px solid #ddd',
                    fontSize: 15,
                    outline: 'none',
                    marginTop: 10,
                  }}
                />
              </>
            )}

            {resetMsg && (
              <div style={{ fontSize: 12.5, color: '#1A56DB', marginTop: 8 }}>
                {resetMsg}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
              <button
                type='button'
                onClick={() => setResetOpen(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: 10,
                  border: '1.5px solid #ddd',
                  background: '#fff',
                  color: '#444',
                  fontWeight: 700,
                  cursor: 'pointer',
                }}>
                Cancel
              </button>
              <button
                type='button'
                disabled={resetLoading}
                onClick={resetStep === 1 ? sendResetOtp : submitReset}
                style={{
                  flex: 2,
                  padding: '12px',
                  borderRadius: 10,
                  border: 'none',
                  background: resetLoading ? '#93c5fd' : '#1A56DB',
                  color: '#fff',
                  fontWeight: 700,
                  cursor: resetLoading ? 'not-allowed' : 'pointer',
                }}>
                {resetLoading
                  ? 'Please wait...'
                  : resetStep === 1
                    ? 'Send OTP'
                    : 'Reset Password'}
              </button>
            </div>

            {resetStep === 2 && (
              <button
                type='button'
                onClick={sendResetOtp}
                disabled={resetLoading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1A56DB',
                  fontSize: 12.5,
                  marginTop: 12,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}>
                Resend OTP
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
